import { Suspense } from 'react';
import { useSEO } from '../../utils/seo';

const LinksPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Weiterführende Links - AfD Bad Windsheim',
    description: 'Weiterführende Links zu AfD Ortsverband Bad Windsheim, AfD Kreisverband Fürth/Neustadt und dem Rahmenprogramm für den AfD-Landesverband Bayern.',
    keywords: 'AfD Links, AfD Ortsverband, AfD Kreisverband, AfD Bayern, Wahlprogramm Bayern',
    canonical: `${siteUrl}/links`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Weiterführende Links',
      description: 'Links zu AfD Verbänden und Programmen',
      url: `${siteUrl}/links`
    }
  });

  const links = [
    {
      title: 'AfD Ortsverband Bad Windsheim und Umgebung',
      description: 'Erfahren Sie mehr über unseren Ortsverband und unsere lokalen Aktivitäten in Bad Windsheim und der Region.',
      url: 'https://afdfuerth-neustadt.de/ortsverband-bad-windsheim/',
      icon: 'ri-community-line',
      color: 'from-primary-600 to-primary-700'
    },
    {
      title: 'AfD Kreisverband Fürth / Neustadt/Aisch',
      description: 'Informationen zu den Verbänden im Kreisverband Fürth und Neustadt an der Aisch.',
      url: 'https://afdfuerth-neustadt.de/verbaende/',
      icon: 'ri-map-2-line',
      color: 'from-secondary-600 to-secondary-700'
    },
    {
      title: 'Rahmenprogramm für den AfD-Landesverband Bayern',
      description: 'Das komplette Wahlprogramm für die Kommunalwahlen 2026 in Bayern als PDF-Dokument.',
      url: 'https://afdbayern.de/wp-content/uploads/2020/02/Wahlprogramm_DIN-A5-Hoch_finales-Komplettprogramm-gem.-LPK.pdf',
      icon: 'ri-file-text-line',
      color: 'from-primary-700 to-primary-800'
    }
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 text-white py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-500 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-6">
              <i className="ri-links-line text-4xl"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Weiterführende Links
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Entdecken Sie weitere Informationen über unsere Verbände und unser Programm für Bayern
            </p>
          </div>
        </div>

        {/* Links Section */}
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="space-y-8">
            {links.map((link, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
              >
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${link.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${link.icon} text-3xl text-white`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                        {link.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {link.description}
                      </p>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold rounded-lg shadow-md hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer group/btn"
                      >
                        <span>Zur Website</span>
                        <i className="ri-arrow-right-line text-lg group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Decorative Bottom Border */}
                <div className={`h-1 bg-gradient-to-r ${link.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-16 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8 border border-primary-100">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                <i className="ri-information-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Weitere Informationen
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Diese Links führen Sie zu weiterführenden Informationen über unsere Verbände und unser politisches Programm. Bei Fragen stehen wir Ihnen gerne zur Verfügung.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default LinksPage;
